import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Building2, ClipboardCheck, LogOut, Menu, X, MessageSquare } from 'lucide-react';
import BrandLogo from './BrandLogo';
import UserFeedbackChat from './UserFeedbackChat';
import { ThemeSwitch } from './ThemeToggle';

function NavItem({ path, label, icon: Icon, isActive, onNavigate }) {
  return (
    <Link
      to={path}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      data-testid={`nav-${label.toLowerCase()}`}
      className={`flex h-10 items-center gap-3 rounded-md px-3 text-[15px] font-bold no-underline transition-colors ${
        isActive ? 'bg-sunken text-ink' : 'text-ink-muted hover:bg-sunken hover:text-ink'
      }`}
    >
      <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
      {label}
      {isActive && <span className="ml-auto h-2 w-2 bg-marker" aria-hidden="true" />}
    </Link>
  );
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/properties', label: 'Properties', icon: Building2 },
  { path: '/inspections', label: 'Inspections', icon: ClipboardCheck },
];

const adminNavItems = [
  { path: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return (parts.map((p) => p[0]).join('').slice(0, 2) || 'U').toUpperCase();
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex min-h-screen bg-paper text-ink">
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-overlay md:hidden"
          onClick={closeMobile}
          aria-label="Close menu"
        />
      )}

      <aside
        className={`fixed z-50 flex h-screen w-60 flex-col gap-0.5 border-r border-line bg-surface px-3 py-5 transition-transform duration-200 motion-reduce:transition-none ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-2 pb-5">
          <Link to="/dashboard" className="flex items-center gap-2.5 text-ink no-underline" onClick={closeMobile}>
            <BrandLogo className="h-[30px] w-[30px]" />
            <span className="font-display text-[19px] font-extrabold" data-testid="sidebar-brand">RealSpect</span>
          </Link>
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-sunken md:hidden" onClick={closeMobile} aria-label="Close menu">
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        <nav aria-label="Main" className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              {...item}
              isActive={location.pathname.startsWith(item.path)}
              onNavigate={closeMobile}
            />
          ))}
          {user?.role === 'admin' && (
            <>
              <p className="m-0 px-3 pb-1 pt-4 text-xs font-bold uppercase tracking-[0.08em] text-ink-muted">Admin</p>
              {adminNavItems.map((item) => (
                <NavItem
                  key={item.path}
                  {...item}
                  isActive={location.pathname.startsWith(item.path)}
                  onNavigate={closeMobile}
                />
              ))}
            </>
          )}
        </nav>

        <div className="mt-auto flex flex-col gap-3 pt-6">
          {user?.is_pro ? (
            <Link to="/upgrade" className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-bold text-ink no-underline hover:bg-sunken" onClick={closeMobile}>
              <span className="inline-flex h-5 items-center rounded-sm bg-marker px-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-on-marker">Pro</span>
              Pilot Pro active
            </Link>
          ) : user?.role !== 'admin' && (
            <Link
              to="/upgrade"
              onClick={closeMobile}
              className="flex flex-col gap-1 rounded-md border border-line bg-sunken p-3 text-ink no-underline hover:border-line-strong"
              data-testid="sidebar-upgrade"
            >
              <span className="text-sm font-bold">Get Pilot Pro</span>
              <span className="text-[13px] leading-[18px] text-ink-muted">PDF reports, tenant links and turnover comparison. One-time $15.</span>
            </Link>
          )}
          <ThemeSwitch />
          <div className="flex items-center gap-3 border-t border-line px-2 pt-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-sunken font-mono text-sm font-bold">
              {initials(user?.full_name)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-[15px] font-bold leading-5" data-testid="user-name">{user?.full_name}</p>
              <p className="m-0 text-[13px] capitalize leading-[18px] text-ink-muted">{user?.role}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-button"
              aria-label="Sign out"
              title="Sign out"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-sunken hover:text-ink"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <main className="w-full min-w-0 flex-1 md:ml-60">
        <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface px-4 py-3 md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line-strong text-ink"
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <BrandLogo className="h-7 w-7" />
          <span className="font-display font-extrabold">RealSpect</span>
        </div>
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>

      <UserFeedbackChat />
    </div>
  );
}
